import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import HeroSection from "@/components/sections/HeroSection";
import SupportedExchanges from "@/components/sections/SupportedExchanges";
import Features from "@/components/sections/Features";
import HowItWorks from "@/components/sections/HowItWorks";
import TopStrategies from "@/components/sections/TopStrategies";
import TrustIndicators from "@/components/sections/TrustIndicators";
import FeaturedOnSection from "@/components/sections/FeaturedOnSection";
import LatestArticles from "@/components/sections/LatestArticles"; 
import SuccessStories from "@/components/sections/SuccessStories";
import BusinessPartnerSection from "@/components/sections/BusinessPartnerSection";
import CallToAction from "@/components/sections/CallToAction";
import Footer from "@/components/Footer";
import { useReferralUrlParams } from "@/hooks/referrals/useReferralUrlParams";
import MetaTags from "@/components/meta/MetaTags";
import { useIsMobile } from "@/hooks/use-mobile";

import ChatOption from "@/components/ChatOption";
import { useAuthState } from "@/hooks/useAuthState";
import PersonalWelcomeSection from "@/components/sections/PersonalWelcomeSection";
import { useFingerprintCompare } from "@/utils/useFingerprint";
import { storeAffiliateIdInCookie, storeCampaignCookie, storeReferralCodeInCookie } from "@/hooks/referrals/referralUtils";
import { isWebview } from '@dvlden/is-webview';
import { setAffiliatedByValue } from "@/utils/affiliationUtils";
import { Capacitor } from "@capacitor/core";

const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { user, isLoading } = useAuthState();

  const { loading: loadcompare, data: compared, compare } = useFingerprintCompare();

  // Use the referral URL parameters hook to handle affiliate IDs and referral codes
  useReferralUrlParams();

  useEffect(() => {
    if(compared){
      if(compared.affiliate_id)
        storeAffiliateIdInCookie(compared.affiliate_id);
      if(compared.campaign)
        storeCampaignCookie(compared.campaign);
      if(compared.referral_code)
        storeReferralCodeInCookie(compared.referral_code);
    }
  }, [compared]);
  
  // Check for authentication
  useEffect(() => {
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      
      // Only redirect to marketplace if explicitly requested by "redirect" parameter
      // Otherwise, let users stay on the index page even if logged in
      const searchParams = new URLSearchParams(location.search);
      const shouldRedirect = searchParams.get("redirect") === "marketplace";
      
      if (data?.session && shouldRedirect) {
        navigate("/marketplace");
      }
    };

    const checkAndCompare = async () => {
      await compare();
    }

    if (isMobile && !user) {
      console.log("[IsWebview] Check if user came via referral/affiliate/campaign.");
      checkAndCompare();
    }

    checkAuth();
  }, [navigate, location.search]);

  return (
    <div className={`min-h-screen bg-white flex flex-col ${isMobile ? 'pb-14' : ''}`}>
      {/* Add MetaTags with explicit image for home page */}
      <MetaTags 
        customImage="/lovable-uploads/bf9cdc94-db08-4840-bae6-869c2f0c41bb.png"
      />
      <Header />
      <main className="flex-grow">
        {user && <PersonalWelcomeSection />}
        <HeroSection />
        <SupportedExchanges />
        <Features />
        <HowItWorks />
        <TopStrategies />
        <TrustIndicators />
        <FeaturedOnSection />
        <LatestArticles />
        <SuccessStories />
        <BusinessPartnerSection />
        <CallToAction />
        {/* Only show ChatOption on desktop */}
        {!isMobile && (
          <ChatOption />
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Index;
